import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import FormField from '@amzn/awsui-components-console/form-field';
import Modal from '@amzn/awsui-components-console/modal';
import Select, { SelectProps } from '@amzn/awsui-components-console/select';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Textarea from '@amzn/awsui-components-console/textarea';
import {
  persistFieldEdit,
  StringFieldEdit,
} from 'src/features/string-group-detail/utils/draftStorage';
import {
  CONTEXT_NOTE_CHAR_LIMIT,
  SOURCE_TEXT_CHAR_LIMIT,
} from 'src/features/string-group-detail/constants';
import {
  buildServerSnapshot,
  EditDraftContext,
  parseBaseVersion,
} from '../utils/editDraft';
import { buildStringGroupPath } from 'src/common/routes';
import LiveRegion from 'src/common/ui/LiveRegion';
import NoPiiNotice from 'src/common/ui/NoPiiNotice';
import {
  ChangeControlEditNotice,
  useChangeControlStatus,
} from 'src/common/ui/ChangeControlBanner';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface CreateVariantModalProps {
  visible: boolean;
  stringId: string;
  /** CX IDs with no variant yet — server list plus the draft's pending ones. */
  availableCxIds: string[];
  /** Draft identity plus the base snapshot the change is written onto. */
  draftContext: EditDraftContext;
  /** The existing variant being edited; absent when creating one. */
  editingVariant?: {
    cxId: string;
    sourceText: string;
    contextNote?: string | null;
  };
  onDismiss: () => void;
  /** Lets the page re-read the draft it just wrote. */
  onDraftSaved?: () => void;
}

/**
 * Creates or edits a CX variant by writing it into the group draft.
 *
 * There is no create-variant mutation: a new variant is the same draft write as
 * a variant edit, with the CX ID chosen rather than fixed (`upsertVariant`
 * appends when the CX ID is new). So saving redirects to the group page, where
 * the user reviews the pending change and submits the group.
 *
 * Inline markup and message format are deliberately NOT offered. They are
 * string-level in TRANSCOM — a variant never overrides them — so the change
 * omits both keys and `applyFieldEdit` leaves the entry's format exactly as it
 * was, including leaving it absent for an UNKNOWN format.
 */
const CreateVariantModal: React.FC<CreateVariantModalProps> = ({
  visible,
  stringId,
  availableCxIds,
  draftContext,
  editingVariant,
  onDismiss,
  onDraftSaved,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isEdit = !!editingVariant;

  /**
   * The change-control window in effect, or `null` on a normal day (and when the
   * status could not be read, which fails open).
   *
   * Read here rather than threaded down from StringDetailPage, for the same
   * reason SourceTextPanel reads it: the answer is a global fact and the hook is
   * `cache-first`, so additional readers cost nothing.
   *
   * Applied in EDIT mode only. Creating a variant is a creation, and the
   * change-control carve-out treats creation as not impacted (design §8.3); the
   * MCM requirement attaches to edits of live content, which is what editing an
   * existing variant is.
   */
  const changeControlWindow = useChangeControlStatus();

  const [selectedCxId, setSelectedCxId] = useState<SelectProps.Option | null>(
    null,
  );
  const [sourceText, setSourceText] = useState('');
  const [contextNote, setContextNote] = useState('');
  /**
   * Why the last save attempt did not persist. While set, the modal stays open
   * with its content so the user can retry or copy the text out.
   */
  const [saveError, setSaveError] = useState<string | undefined>(undefined);

  // Reset on open rather than on mount: a previous attempt's content or error
  // must not greet the next open.
  useEffect(() => {
    if (visible) {
      setSelectedCxId(
        editingVariant
          ? { label: editingVariant.cxId, value: editingVariant.cxId }
          : null,
      );
      setSourceText(editingVariant?.sourceText ?? '');
      setContextNote(editingVariant?.contextNote ?? '');
      setSaveError(undefined);
    }
  }, [visible, editingVariant]);

  const trimmedSourceText = sourceText.trim();
  const trimmedContextNote = contextNote.trim();

  // Source text and context note are both required. Both are capped at the
  // shared limits. Edit mode keeps the note requirement on purpose: the UI
  // policy is every variant carries a note, so a note-less variant created
  // via the API gains one on its first edit here.
  const sourceTextError = !trimmedSourceText
    ? t('sgd-create-variant-source-text-required')
    : trimmedSourceText.length > SOURCE_TEXT_CHAR_LIMIT
      ? t('sgd-variant-source-text-too-long', { max: SOURCE_TEXT_CHAR_LIMIT })
      : undefined;
  const contextNoteError = !trimmedContextNote
    ? t('sgd-create-context-note-required')
    : trimmedContextNote.length > CONTEXT_NOTE_CHAR_LIMIT
      ? t('sgd-context-note-too-long', { max: CONTEXT_NOTE_CHAR_LIMIT })
      : undefined;

  const isSaveDisabled =
    !selectedCxId || !!sourceTextError || !!contextNoteError;

  const handleSave = (cxId: string) => {
    // Only the one variant this modal authored is expressed, so an existing
    // draft entry keeps its other pending values. No format keys — see the
    // component doc.
    const change: StringFieldEdit = {
      kind: 'variant',
      cxId,
      sourceText: trimmedSourceText,
      contextNote: trimmedContextNote,
    };

    try {
      persistFieldEdit(
        draftContext.namespaceId,
        draftContext.stringGroupId,
        parseBaseVersion(draftContext.stringGroupVersion),
        buildServerSnapshot(draftContext),
        change,
      );
    } catch (err) {
      // The draft is the ONLY copy of this variant, so a rejected write must not
      // close the modal or navigate.
      setSaveError(
        err instanceof Error && err.message
          ? err.message
          : t('error-unknown-storage'),
      );
      return;
    }

    setSaveError(undefined);
    onDraftSaved?.();
    onDismiss();
    navigate(
      buildStringGroupPath(
        draftContext.namespaceId,
        draftContext.stringGroupId,
      ),
    );
  };

  if (!visible) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={
        isEdit
          ? t('string-variant-modal-edit-header', { stringId })
          : t('string-variant-modal-create-header', { stringId })
      }
      footer={
        <Box float="right">
          <SpaceBetween size="xs" direction="horizontal">
            <Button
              variant="link"
              onClick={onDismiss}
              data-testid="create-variant-cancel-button"
            >
              {t('string-cancel')}
            </Button>
            <Button
              variant="primary"
              // The CX ID re-check is TypeScript narrowing only — Save is
              // disabled until one is selected.
              onClick={() => {
                if (selectedCxId?.value) {
                  handleSave(selectedCxId.value);
                }
              }}
              disabled={isSaveDisabled}
              data-testid="create-variant-save-button"
            >
              {isEdit ? t('sgd-save') : t('string-save-changes')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {/* Bounded, scrollable body: on a short viewport the fields + alerts can
          exceed the available height, so the body scrolls within the modal
          while the header and footer (Save/Cancel) stay pinned and visible. The
          CX ID Select renders its dropdown in a portal, so overflow here does
          not clip it. */}
      <div
        data-testid="create-variant-modal"
        style={{ maxHeight: '60vh', overflowY: 'auto' }}
      >
        <SpaceBetween size="m">
          <NoPiiNotice />
          {/* `null` in create mode: the component's exemption contract — see the
              `changeControlWindow` doc above for why creation is out of scope. */}
          <ChangeControlEditNotice
            changeControlWindow={isEdit ? changeControlWindow : null}
          />

          {saveError && (
            <Alert
              type="error"
              header={t('string-unsaved-changes')}
              data-testid="create-variant-save-error"
            >
              {/* `saveError` is the browser's own message and is passed through
                  untranslated; the surrounding sentence is a single resource so
                  each locale controls its own word order. */}
              {t('string-draft-save-failed', { detail: saveError })}
            </Alert>
          )}
          {/* The Alert above is role="group" and not announced. This mirror
              makes the failure audible the moment it appears. */}
          <LiveRegion assertive>
            {saveError
              ? t('string-draft-save-failed-live', { detail: saveError })
              : ''}
          </LiveRegion>
          <Alert type="warning" data-testid="create-variant-warning">
            {t('string-variant-semantics-warning')}
          </Alert>
          <FormField
            label={t('string-variant-cx-id-required-label')}
            constraintText={
              isEdit
                ? t('string-variant-cx-id-immutable')
                : t('string-variant-cxid-choose-one')
            }
          >
            <Select
              selectedOption={selectedCxId}
              placeholder={t('string-variant-cxid-placeholder')}
              // Editing keeps the CX ID fixed: it identifies the variant.
              disabled={isEdit}
              options={(isEdit && editingVariant
                ? [editingVariant.cxId]
                : availableCxIds
              ).map((cxId) => ({
                label: cxId,
                value: cxId,
              }))}
              onChange={({ detail }) => setSelectedCxId(detail.selectedOption)}
              data-testid="create-variant-cxid-select"
            />
          </FormField>
          <FormField
            label={t('string-field-variant-source-text-required')}
            errorText={sourceTextError}
            constraintText={t('field-character-limit', {
              limit: SOURCE_TEXT_CHAR_LIMIT,
            })}
          >
            <Textarea
              value={sourceText}
              onChange={({ detail }) => setSourceText(detail.value)}
              rows={4}
              data-testid="create-variant-source-text"
            />
          </FormField>
          <FormField
            label={t('string-field-context-note-required')}
            errorText={contextNoteError}
            constraintText={t('field-character-limit', {
              limit: CONTEXT_NOTE_CHAR_LIMIT,
            })}
          >
            <Textarea
              value={contextNote}
              onChange={({ detail }) => setContextNote(detail.value)}
              placeholder={t('string-context-note-help')}
              rows={3}
              data-testid="create-variant-context-note"
            />
          </FormField>
        </SpaceBetween>
      </div>
    </Modal>
  );
};

export default CreateVariantModal;
